import { StyleSheet, css } from 'aphrodite';
import { useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Avatar } from '../../../web/components-v2/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { accessibilityStyles } from '../../../web/styles/styles';
import { apiErrorMessage } from '../../focused-view/data/apiErrorMessage';
import { ITimeEntryReview, TimeEntryReviewAction, useReviewTimeEntryMutation } from '../../queries/TimeEntry';
import {
	activitySpanMinutes,
	formatDateTime,
	formatDayShort,
	formatDuration,
	formatTime,
	minutesOverActivitySpan,
} from '../../timeTracking/days';
import { LoadError } from '../../timeTracking/components/LoadError';
import { SubmitNote } from '../../timeTracking/components/SubmitNote';
import { formatHours } from '../../timeTracking/hours';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { ReasonDialog } from './ReasonDialog';
import { OVER_SPAN_WARNING_MINUTES } from './reviewFilters';

const COLUMN_COUNT = 7;

export function ReviewQueueTable({
	reviews,
	isLoading,
	isError,
	onRetry,
	canActAsSelf,
	nameFor,
	initialsFor,
	onOpenRep,
}: {
	reviews: ITimeEntryReview[];
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	canActAsSelf: boolean;
	nameFor: (userId?: string) => string;
	initialsFor: (userId?: string) => string;
	onOpenRep: (userId: string) => void;
}) {
	const userSession = useUserSession();
	const [sendingBack, setSendingBack] = useState<ITimeEntryReview | null>(null);
	const [inFlight, setInFlight] = useState<Record<string, TimeEntryReviewAction>>({});

	const review = useReviewTimeEntryMutation({
		onError: (err, { action }) =>
			toast.error(apiErrorMessage(err, action === 'approve' ? 'Failed to approve hours' : 'Failed to send hours back')),
		onSuccess: (_, { action, id }) => {
			toast.success(
				action === 'approve' ? 'Hours approved' : 'Hours sent back. The rep gets an email with your comment.'
			);
			if (action === 'reject') {
				setSendingBack(current => (current?.entry.id === id ? null : current));
			}
		},
	});

	const runReview = (variables: { id: string; action: TimeEntryReviewAction; comment?: string }) => {
		setInFlight(current => ({ ...current, [variables.id]: variables.action }));
		review.mutate(variables, {
			onSettled: () =>
				setInFlight(current => {
					const next = { ...current };
					delete next[variables.id];
					return next;
				}),
		});
	};

	return (
		<>
			<section className={css(tt.card, tt.tableCard)} aria-label='Days waiting for review'>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn sticky>Rep</DataGridColumn>
						<DataGridColumn sticky>Day</DataGridColumn>
						<DataGridColumn sticky>Hours entered</DataGridColumn>
						<DataGridColumn sticky>Coffee activity</DataGridColumn>
						<DataGridColumn sticky>Talk time</DataGridColumn>
						<DataGridColumn sticky>Submitted</DataGridColumn>
						<DataGridColumn sticky>
							<span className={css(accessibilityStyles.srOnly)}>Actions</span>
						</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{isLoading ? (
							<DataGridRow>
								<DataGridRowItem colSpan={COLUMN_COUNT}>
									<LoadingSpinner type='large' />
								</DataGridRowItem>
							</DataGridRow>
						) : isError ? (
							<DataGridRow>
								<DataGridRowItem colSpan={COLUMN_COUNT}>
									<LoadError message='We couldn’t load the review queue.' onRetry={onRetry} />
								</DataGridRowItem>
							</DataGridRow>
						) : reviews.length ? (
							reviews.map(item => {
								const { entry, activity } = item;
								const span = activitySpanMinutes(activity);
								const overSpan = minutesOverActivitySpan(entry.hours, activity);
								const isOwnDay = entry.userId === userSession.user?.id;
								const pendingAction = inFlight[entry.id];
								const isPending = !!pendingAction;
								return (
									<DataGridRow key={entry.id}>
										<DataGridRowItem>
											<Button
												type='button'
												variant='ghost'
												styles={[styles.rep]}
												onClick={() => onOpenRep(entry.userId)}
											>
												<Avatar
													fallbackText={initialsFor(entry.userId)}
													altText={nameFor(entry.userId)}
													smallText
													styles={[styles.avatar]}
												/>
												<span className={css(styles.repText)}>
													<span className={css(styles.repName, tt.truncate)}>{nameFor(entry.userId)}</span>
													<span className={css(styles.sub)}>{entry.officeLocation ?? '—'}</span>
												</span>
											</Button>
										</DataGridRowItem>
										<DataGridRowItem styles={[styles.nowrap]}>{formatDayShort(entry.day)}</DataGridRowItem>
										<DataGridRowItem>
											<span className={css(styles.stack)}>
												<span className={css(tt.number, styles.hours)}>{formatHours(entry.hours)}</span>
												<SubmitNote note={entry.submitNote} />
												{entry.exceptionOpenedByUserId ? (
													<Pill variant='label' color='purple' label='Opened by admin' />
												) : entry.reviewedAt ? (
													<Pill variant='label' color='blue' label='Resubmitted' />
												) : null}
											</span>
										</DataGridRowItem>
										<DataGridRowItem>
											<span className={css(styles.stack)}>
												{span === undefined ? (
													<span className={css(styles.sub)}>No calls logged</span>
												) : (
													<span>
														{formatTime(activity?.firstActivityAt)} – {formatTime(activity?.lastActivityAt)} ·{' '}
														{formatDuration(span)}
													</span>
												)}
												{overSpan >= OVER_SPAN_WARNING_MINUTES ? (
													<span className={css(styles.warning)}>
														<WarningIcon className={css(styles.warningIcon)} />
														{formatDuration(overSpan)} more than the activity span
													</span>
												) : null}
												{entry.exceptionReason ? (
													<span className={css(styles.sub, tt.truncate)}>“{entry.exceptionReason}”</span>
												) : null}
											</span>
										</DataGridRowItem>
										<DataGridRowItem>
											{activity?.callCount ? formatDuration(activity.talkTimeSeconds / 60) : '—'}
										</DataGridRowItem>
										<DataGridRowItem styles={[styles.sub, styles.nowrap]}>
											{formatDateTime(entry.submittedAt)}
										</DataGridRowItem>
										<DataGridRowItem styles={[tt.actionCell]}>
											<span className={css(styles.actions)}>
												<Button
													type='button'
													variant='outline'
													size='sm'
													disabled={!canActAsSelf || isOwnDay || isPending}
													onClick={() => setSendingBack(item)}
												>
													Send back
												</Button>
												<Button
													type='button'
													variant='success'
													size='sm'
													disabled={!canActAsSelf || isOwnDay || isPending}
													isLoading={pendingAction === 'approve'}
													onClick={() => runReview({ action: 'approve', id: entry.id })}
												>
													Approve
												</Button>
											</span>
										</DataGridRowItem>
									</DataGridRow>
								);
							})
						) : (
							<DataGridRow>
								<DataGridRowItem colSpan={COLUMN_COUNT} styles={[tt.emptyCell]}>
									Nothing is waiting for review.
								</DataGridRowItem>
							</DataGridRow>
						)}
					</DataGridRows>
				</DataGrid>
				<p className={css(tt.muted, styles.footer)}>
					Coffee activity comes from the rep’s calls and is for reference only. It never changes the hours they entered.
				</p>
			</section>
			<ReasonDialog
				open={!!sendingBack}
				onOpenChange={open => !open && setSendingBack(null)}
				title={sendingBack ? `Send back ${nameFor(sendingBack.entry.userId)}'s hours` : 'Send back hours'}
				description={
					sendingBack
						? `${formatDayShort(sendingBack.entry.day)} · ${formatHours(sendingBack.entry.hours)} hours entered`
						: undefined
				}
				label='Comment for the rep'
				help='They see this in Coffee and get it by email. You can’t change their hours yourself.'
				confirmLabel='Send back'
				isPending={!!sendingBack && !!inFlight[sendingBack.entry.id]}
				onConfirm={comment => sendingBack && runReview({ action: 'reject', comment, id: sendingBack.entry.id })}
			/>
		</>
	);
}

const styles = StyleSheet.create({
	actions: {
		display: 'flex',
		gap: SPACE[8],
		justifyContent: 'flex-end',
	},
	avatar: {
		flexShrink: 0,
		height: 32,
		width: 32,
	},
	footer: {
		fontSize: THEME.typography.fontSizes.xs,
		padding: `${SPACE[12]}px ${SPACE[20]}px`,
	},
	hours: {
		fontSize: THEME.typography.fontSizes.md,
	},
	nowrap: {
		whiteSpace: 'nowrap',
	},
	rep: {
		gap: SPACE[8],
		height: 'auto',
		justifyContent: 'flex-start',
		maxWidth: 240,
		minWidth: 0,
		padding: 0,
		textAlign: 'left',
	},
	repName: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	repText: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
	},
	stack: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
	},
	sub: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	warning: {
		alignItems: 'center',
		color: THEME.colors.warning.dark,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
	},
	warningIcon: {
		flexShrink: 0,
		height: 12,
		width: 12,
	},
});
